import { mix } from 'polished';

import { isDarkBackground } from './isDarkBackground';

// Elevation steps for the neutral surface scale, expressed as the fraction of
// white (dark mode) / black (light mode) mixed into the tenant secondary_color.
// Cards (surface) sit just above the base; hover/active higher; muted highest.
// Borders track the mid steps so they read against their surface.
const STEPS: Record<string, number> = {
  '--bg-surface': 0.06,
  '--bg-hover': 0.09,
  '--bg-active': 0.13,
  '--bg-muted': 0.2,
  '--border-default': 0.14,
  '--border-strong': 0.22,
};

/**
 * Build the neutral surface scale from the tenant's secondary_color so the whole
 * background/elevation system is one coherent tint of the brand background.
 *
 * The base (`--bg-default`) is the secondary color itself; every other surface is
 * mixed toward white on a dark secondary (elevated = lighter) or toward black on
 * a light secondary (elevated = darker), so cards always contrast the base. Cards
 * and popovers map to the elevated `--bg-surface` (they default to `--bg-default`
 * via the @theme bridge, which would make them identical to the base). Text/icon
 * tokens are intentionally NOT derived — they stay light-on-dark / dark-on-light
 * from tokens.css so readability holds for any brand color.
 *
 * Returns the CSS-variable map to apply, or `{}` when the color is unparseable
 * (so the caller leaves the static token defaults in place).
 */
export const deriveSurfaceScale = (
  secondary: string
): Record<string, string> => {
  try {
    const toward = isDarkBackground(secondary) ? '#FFFFFF' : '#000000';
    const scale: Record<string, string> = { '--bg-default': secondary };
    Object.entries(STEPS).forEach(([token, amount]) => {
      scale[token] = mix(1 - amount, secondary, toward);
    });
    scale['--color-card'] = scale['--bg-surface'];
    scale['--color-popover'] = scale['--bg-surface'];
    return scale;
  } catch {
    return {};
  }
};
